import app from 'flarum/forum/app';
import { override } from 'flarum/common/extend';
import IndexPage from 'flarum/forum/components/IndexPage';
import SectionHero from '../components/SectionHero';

/**
 * Replace / prepend a single brand-consistent BlueEmotion section hero (showing
 * only the page title) across the main listing pages:
 *
 *  - Discussions  (core IndexPage)                 — was the admin "welcome banner".
 *  - Following    (core IndexPage, `following` route via flarum/subscriptions).
 *  - Polls        (fof/polls PollsShowcasePage)    — was the fof PollPageHero.
 *  - Tags         (flarum/tags TagsPage)           — was the tags hero.
 *  - Badges       (fof/badges BadgesPage)          — had no hero at all.
 *  - Messages     (flarum/messages MessagesPage)   — was the MessagesPageHero.
 *  - New poll     (fof/polls ComposePollPage)      — was the fof PollPageHero.
 *
 * Pages that expose a `hero()` method get a clean OVERRIDE (it returns a single
 * component, so we replace rather than extend). BadgesPage has no `hero()`, so
 * we OVERRIDE `view()` and render the hero as a full-width sibling above it.
 *
 * The optional-extension pages are patched by STRING PATH (the `ext:` form).
 * `override(path, ...)` routes through `flarum.reg.onLoad`, so the patch is
 * applied the moment the target module registers — or immediately if it already
 * has — regardless of bundle load order, and is a silent no-op when the
 * extension isn't installed. This avoids the eager-`ext:`-import race (a
 * top-level `const x = flarum.reg.get(...)` can be `undefined` if our bundle
 * evaluates before the target's), so no `app.initializers.has()` guard is
 * needed.
 */
export default function addSectionHeroes() {
  // Discussions — core IndexPage, always loaded. The flarum/subscriptions "Following"
  // page reuses this same component on the `following` route, so switch the title
  // accordingly.
  override(IndexPage.prototype, 'hero', () => {
    const key = app.current.get('routeName') === 'following' ? 'following' : 'discussions';
    return SectionHero.component({
      title: app.translator.trans("glowingblue-demo.forum.heroes.".concat(key))
    });
  });

  // Polls — fof/polls.
  override('ext:fof/polls/forum/components/PollsShowcasePage', 'hero', () => SectionHero.component({
    title: app.translator.trans('glowingblue-demo.forum.heroes.polls')
  }));

  // Tags — flarum/tags.
  override('ext:flarum/tags/forum/components/TagsPage', 'hero', () => SectionHero.component({
    title: app.translator.trans('glowingblue-demo.forum.heroes.tags')
  }));

  // Badges — fof/badges (no hero() method, so wrap view()).
  override('ext:fof/badges/forum/components/BadgesPage', 'view', function (original) {
    return [SectionHero.component({
      title: app.translator.trans('glowingblue-demo.forum.heroes.badges')
    }), original()];
  });

  // Messages — flarum/messages.
  override('ext:flarum/messages/forum/components/MessagesPage', 'hero', () => SectionHero.component({
    title: app.translator.trans('glowingblue-demo.forum.heroes.messages')
  }));

  // New poll — fof/polls compose page.
  override('ext:fof/polls/forum/components/ComposePollPage', 'hero', () => SectionHero.component({
    title: app.translator.trans('glowingblue-demo.forum.heroes.compose_poll')
  }));
}
flarum.reg.add('glowingblue-demo', 'forum/extenders/addSectionHeroes', addSectionHeroes);